<template>
  <div
    class="form-group row"
    data-testid="lookbackwindow">
    <form-label
      for-i-d="edit_audiencerule_name"
      :is-required="true"
      >{{ capitalize($t('names.lookbackWindow', { n: 1 })) }}
    </form-label>
    <div class="col-sm-8">
      <div class="toggle single">
        <toggle-button
          :value="isLookBackWindowRelative"
          :sync="true"
          @change="toggleLookBackWindowRelative()" />
        <span>{{ $t('messages.relativeToDateOfProcessingByDMP') }}</span>
      </div>
    </div>
    <template v-if="isLookBackWindowRelative">
      <div class="offset-sm-3 col-sm-2">
        <input
          id="edit_audiencelookbackwindow_timeperiod"
          v-model="lookBackWindowTimePeriod"
          data-testid="lookbackwindowTimePeriod"
          name="timePeriod"
          type="number"
          min="1"
          :max="maxLookBackWindowTimePeriod"
          :class="{
            'form-control': true,
            input: true,
            'is-danger': validator.messageBag.errors.has('timePeriod'),
          }"
          @blur="adjustLookbackWindowTimePeriodIfNeeded" />
      </div>
      <div
        id="edit_audiencelookbackwindow_periodtype"
        class="col-sm-3">
        <multiselect
          v-model="lookBackWindowPeriodType"
          data-testid="lookbackwindowPeriodType"
          track-by="value"
          label="name"
          :options="lookBackWindowPeriodTypes"
          :searchable="true"
          :show-labels="false"
          :allow-empty="false"
          @select="adjustLookbackWindowTimePeriodIfNeeded" />
      </div>
    </template>
    <template v-else>
      <div class="offset-sm-3 col-sm-8">
        <div class="row">
          <!-- FROM DATE -->
          <div
            class="col-6"
            forID="edit_lookbackwindow_from_date">
            {{ capitalize($t('adverbs.from')) }}
          </div>
          <!-- TO DATE -->
          <div
            class="col-6"
            forID="edit_lookbackwindow_to_date">
            {{ capitalize($t('adverbs.to')) }}
          </div>
          <div class="col-6 mt-1">
            <date-picker
              id="edit_lookbackwindow_from_date"
              :date="lookBackWindowFromDate"
              :allow-empty="false"
              name="fromDate"
              class="daterangepicker-input"
              :class="{ 'is-danger': validator.messageBag.errors.has('fromDate') }"
              :min-date="minDate"
              :max-date="maxStartDate"
              @date-changed="
                (date) => {
                  lookBackWindowFromDate = date;
                }
              " />
            <span
              v-show="validator.messageBag.errors.has('fromDate')"
              class="help is-danger"
              >{{ validator.messageBag.errors.first('fromDate') }}</span
            >
          </div>
          <div class="col-6 mt-1">
            <date-picker
              id="edit_lookbackwindow_to_date"
              :date="lookBackWindowToDate"
              :allow-empty="false"
              name="toDate"
              class="daterangepicker-input"
              :class="{ 'is-danger': validator.messageBag.errors.has('toDate') }"
              :min-date="lookBackWindowFromDate"
              :max-date="maxDate"
              @date-changed="
                (date) => {
                  lookBackWindowToDate = date;
                }
              " />
            <span
              v-show="validator.messageBag.errors.has('toDate')"
              class="help is-danger"
              >{{ validator.messageBag.errors.first('toDate') }}</span
            >
          </div>
        </div>
      </div>
    </template>
    <template v-if="showCandidateFrequency">
      <div class="offset-sm-3 col-sm-9 mt-3">
        <p>{{ $t('messages.lookbackWindowCandidateFrequencyDescription') }}</p>
      </div>
      <div class="offset-sm-3 col-sm-2">
        <input
          id="edit_audiencelookbackwindow_candidatefrequency"
          v-model="lookBackWindowCandidateFrequency"
          data-testid="lookbackwindowCandidateFrequency"
          name="candidateFrequency"
          type="number"
          min="1"
          max="100"
          :class="{
            'form-control': true,
            input: true,
            'is-danger': validator.messageBag.errors.has('candidateFrequency'),
          }" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { DateTime } from 'luxon';
  import { capitalize, computed, onMounted } from 'vue';
  import { useStore } from 'vuex';

  import { DatePicker, FormLabel } from '@/common/components/controls';
  import Granularity from '@/common/enums/granularity.enum';
  import AudienceLookBackWindow from '@/common/models/audience/AudienceLookBackWindow';
  import AudienceLookBackWindowPeriodTypes from '@/common/models/audience/AudienceLookBackWindowPeriodTypes';
  import Validator from '@/common/models/audience/Validator';

  // Setup
  const props = defineProps<{
    showCandidateFrequency: boolean;
    audienceLookbackWindow: AudienceLookBackWindow;
    validator: Validator;
  }>();
  const store = useStore();

  // Mount
  onMounted(() => {
    if (lookBackWindowFromDate.value == null) {
      lookBackWindowFromDate.value = minDate;
    }
    if (lookBackWindowToDate.value == null) {
      lookBackWindowToDate.value = maxDate;
    }
  });

  // Constants
  const lookBackWindowPeriodTypes = AudienceLookBackWindowPeriodTypes.options();
  const maxDate = DateTime.local().startOf('day');
  const minDate = maxDate.minus({ years: 2 });

  // Computed
  const isLookBackWindowRelative = computed({
    get() {
      return props.audienceLookbackWindow !== null ? props.audienceLookbackWindow.isRelative : null;
    },
    set(value) {
      store.dispatch('updateAudienceLookBackWindowIsRelative', {
        AudienceLookBackWindow: props.audienceLookbackWindow,
        value,
      });
    },
  });
  const lookBackWindowTimePeriod = computed({
    get() {
      return props.audienceLookbackWindow !== null ? props.audienceLookbackWindow.timePeriod : null;
    },
    set(value) {
      store.dispatch('updateAudienceLookBackWindowTimePeriod', {
        AudienceLookBackWindow: props.audienceLookbackWindow,
        value,
      });
    },
  });
  const lookBackWindowPeriodType = computed<Granularity>({
    get() {
      if (props.audienceLookbackWindow === null) {
        return null;
      }
      return lookBackWindowPeriodTypes.find((x) => x.value === props.audienceLookbackWindow.periodType);
    },
    set(value) {
      if (props.audienceLookbackWindow === null) {
        return;
      }
      store.dispatch('updateAudienceLookBackWindowPeriodType', {
        AudienceLookBackWindow: props.audienceLookbackWindow,
        value: value.value,
      });
    },
  });
  const maxLookBackWindowTimePeriod = computed(() => {
    switch (lookBackWindowPeriodType.value.value) {
      case Granularity.DAY:
        return 365;
      case Granularity.MONTH:
        return 12;
      case Granularity.YEAR:
        return 1;
      default:
        return 100;
    }
  });
  const lookBackWindowFromDate = computed({
    get() {
      return props.audienceLookbackWindow !== null ? props.audienceLookbackWindow.fromDate : null;
    },
    set(value) {
      store.dispatch('updateAudienceLookBackWindowFromDate', {
        AudienceLookBackWindow: props.audienceLookbackWindow,
        value,
      });
    },
  });
  const lookBackWindowToDate = computed({
    get() {
      return props.audienceLookbackWindow !== null ? props.audienceLookbackWindow.toDate : null;
    },
    set(value) {
      store.dispatch('updateAudienceLookBackWindowToDate', {
        AudienceLookBackWindow: props.audienceLookbackWindow,
        value,
      });
    },
  });
  const maxStartDate = computed(() => (maxDate < lookBackWindowToDate.value ? maxDate : lookBackWindowToDate.value));
  const lookBackWindowCandidateFrequency = computed({
    get() {
      return props.audienceLookbackWindow !== null ? props.audienceLookbackWindow.candidateFrequency : null;
    },
    set(value) {
      store.dispatch('updateAudienceLookBackWindowCandidateFrequency', {
        AudienceLookBackWindow: props.audienceLookbackWindow,
        value,
      });
    },
  });

  // Methods
  function toggleLookBackWindowRelative() {
    isLookBackWindowRelative.value = !isLookBackWindowRelative.value;
  }
  function adjustLookbackWindowTimePeriodIfNeeded() {
    setTimeout(() => {
      if (lookBackWindowTimePeriod.value > maxLookBackWindowTimePeriod.value) {
        lookBackWindowTimePeriod.value = maxLookBackWindowTimePeriod.value;
      } else if (lookBackWindowTimePeriod.value < 1) {
        lookBackWindowTimePeriod.value = 1;
      }
    }, 100);
  }
</script>

<style lang="scss"></style>
